import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { GetPaidMetricGrid, GetPaidMetric } from "./GetPaidPrimitives";

interface CorrectionItem {
  id: string;
  phoneme?: string;
  local?: string;
  french?: string;
  userTranslation?: string;
  audioUrl?: string;
}

interface CorrectionDetails {
  id: string;
  userName: string;
  phonemes: CorrectionItem[];
  phrases: CorrectionItem[];
  translations: CorrectionItem[];
}

interface ModeratorCorrectionDetailsProps {
  details: CorrectionDetails;
  t: any;
  itemScores: Record<string, number | undefined>;
  setScore: (id: string, value: string) => void;
  savingScores: boolean;
  averages: { ph: number; pp: number; tp: number };
  totalWeighted: number;
  canValidate: boolean;
  onSaveScores: () => void;
  onFinishCorrection: () => void;
  onBack: () => void;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/58">
      {children}
    </p>
  );
}

function ScorePanel({ id, value, onChange, t }: {
  id: string; value: string; onChange: (v: string) => void; t: any;
}) {
  return (
    <div className="rounded-[1.1rem] border border-border/40 bg-background/24 p-3 sm:p-4">
      <label className="text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground/62">
        {t.getPaid.score} · /10
      </label>
      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Input
          id={`score-${id}`}
          type="number"
          min="0" max="10" step="0.5"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t.getPaid.scorePlaceholder}
          className="h-10 rounded-2xl border-border/40 bg-transparent px-4 text-sm shadow-none placeholder:text-muted-foreground/45 focus-visible:ring-1 focus-visible:ring-brand/45"
        />
      </div>
    </div>
  );
}

function CorrectionCard({ children }: { children: React.ReactNode }) {
  return (
    <article className="rounded-[1.35rem] border border-border/40 bg-transparent px-4 py-4 transition-colors hover:border-brand/16 sm:px-5 lg:px-6">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_14rem] xl:items-start">
        {children}
      </div>
    </article>
  );
}

export function ModeratorCorrectionDetails({
  details, t, itemScores, setScore,
  savingScores, averages, totalWeighted, canValidate,
  onSaveScores, onFinishCorrection, onBack,
}: ModeratorCorrectionDetailsProps) {
  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
          {t.getPaid.correctingTestFor.replace('{name}', details.userName)}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack}
            className="h-8 rounded-full border-border/40 bg-transparent px-3 text-xs text-muted-foreground hover:bg-slate-900/[0.03] dark:hover:bg-white/[0.03] hover:text-foreground">
            <Icon name="arrowLeft" className="mr-1.5 h-3.5 w-3.5" />
            {t.getPaid.backToList}
          </Button>
          <Button variant="secondary" size="sm" onClick={onSaveScores} disabled={savingScores}
            className="h-8 rounded-full border border-border/40 px-3 text-xs">
            {savingScores ? t.getPaid.saving : t.getPaid.save}
          </Button>
          <Button size="sm" onClick={onFinishCorrection} disabled={savingScores || !canValidate}
            className="h-8 rounded-full border border-success/20 bg-success/[0.085] px-3 text-xs text-success shadow-none hover:border-success/36 hover:bg-success/[0.13] hover:text-success">
            <Icon name="check" className="mr-1.5 h-3.5 w-3.5" />
            {t.getPaid.finishCorrection}
          </Button>
        </div>
      </div>

      <GetPaidMetricGrid>
        <GetPaidMetric label={t.getPaid.phonemesWeight} value={`${Math.round(averages.ph * 100) / 100}/10`} />
        <GetPaidMetric label={t.getPaid.phrasesWeight} value={`${Math.round(averages.pp * 100) / 100}/10`} />
        <GetPaidMetric label={t.getPaid.translationsWeight} value={`${Math.round(averages.tp * 100) / 100}/10`} />
        <GetPaidMetric
          label={t.getPaid.totalWeightedScore}
          value={`${Math.round(totalWeighted * 100) / 100}/10`}
          tone={totalWeighted >= 5 ? "emerald" : "amber"}
        />
      </GetPaidMetricGrid>

      <div className="space-y-3">
        <SectionLabel>{t.getPaid.sectionPhonemes}</SectionLabel>
        <div className="space-y-2">
          {details.phonemes.map((p: any) => (
            <CorrectionCard key={p.id}>
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-medium text-foreground">{p.phoneme}</p>
                {p.audioUrl ? (
                  <audio src={p.audioUrl} controls className="w-full max-w-sm" />
                ) : (
                  <p className="text-xs text-muted-foreground/64">{t.getPaid.audioNotProvided}</p>
                )}
              </div>
              <ScorePanel
                id={p.id}
                value={typeof itemScores[p.id] === "number" ? String(itemScores[p.id]) : ""}
                onChange={(v) => setScore(p.id, v)}
                t={t}
              />
            </CorrectionCard>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <SectionLabel>{t.getPaid.sectionPhrases}</SectionLabel>
        <div className="space-y-2">
          {details.phrases.map((pp: any) => (
            <CorrectionCard key={pp.id}>
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-medium text-foreground">{pp.local}</p>
                {pp.french && <p className="text-xs text-muted-foreground/64">{pp.french}</p>}
                {pp.audioUrl ? (
                  <audio src={pp.audioUrl} controls className="w-full max-w-sm" />
                ) : (
                  <p className="text-xs text-muted-foreground/64">{t.getPaid.audioNotProvided}</p>
                )}
              </div>
              <ScorePanel
                id={pp.id}
                value={typeof itemScores[pp.id] === "number" ? String(itemScores[pp.id]) : ""}
                onChange={(v) => setScore(pp.id, v)}
                t={t}
              />
            </CorrectionCard>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <SectionLabel>{t.getPaid.sectionTranslations}</SectionLabel>
        <div className="space-y-2">
          {details.translations.map((tp: any) => (
            <CorrectionCard key={tp.id}>
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-medium text-foreground">{tp.local}</p>
                <p className="text-sm text-muted-foreground/72">{tp.userTranslation}</p>
              </div>
              <ScorePanel
                id={tp.id}
                value={typeof itemScores[tp.id] === "number" ? String(itemScores[tp.id]) : ""}
                onChange={(v) => setScore(tp.id, v)}
                t={t}
              />
            </CorrectionCard>
          ))}
        </div>
      </div>
    </section>
  );
}
